import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useInView } from 'motion/react';
import { cn, safeClone, sanitizeChartData } from '../lib/utils';
import { getProgressSummary } from '../services/nutritionService';
import { ChevronDown, Activity, LayoutTemplate } from 'lucide-react';
import { triggerHaptic } from '../lib/haptic';

import { MealAnalysis, UserGoals, Language, Vibe } from '../types';

interface ChartCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode | ((representation: 'line' | 'bar') => React.ReactNode);
  chartData: any[];
  period: string;
  lang: Language;
  vibe: Vibe;
  illumination?: boolean;
  type?: 'food' | 'water' | 'both' | 'caffeine' | 'blue' | 'golden' | 'orange' | 'streak' | 'achievement';
  defaultOpen?: boolean;
  headerActions?: React.ReactNode;
}

const globalSummaryCache: Record<string, string> = {};

export const ChartCard: React.FC<ChartCardProps> = ({ 
  title, 
  icon, 
  children, 
  chartData, 
  period, 
  lang, 
  vibe, 
  illumination, 
  type = 'both',
  defaultOpen = true,
  headerActions
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [representation, setRepresentation] = useState<'line' | 'bar'>('bar');
  const [isFlipped, setIsFlipped] = useState(false);
  const [summary, setSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(cardRef, { once: true, margin: '200px' });



  const chartDataSerialized = useMemo(() => {
    return JSON.stringify(sanitizeChartData(chartData));
  }, [chartData]);

  useEffect(() => {
    if (!isInView) return;
    
    const sanitizedData = sanitizeChartData(chartData);
    const dataString = chartDataSerialized;
    const memoryCacheKey = `${period}-${lang}-${vibe}-${dataString}`;
    
    // 1. Check in-memory cache first
    if (globalSummaryCache[memoryCacheKey]) {
      setSummary(prev => prev !== globalSummaryCache[memoryCacheKey] ? globalSummaryCache[memoryCacheKey] : prev);
      return;
    }

    // 2. Check localStorage (to survive tab closes/reloads)
    // We use title to group the caches so we don't bloat localStorage, overwriting old data for the same chart
    const storageKey = `chart_summary_${title}_${period}_${lang}_${vibe}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.dataString === dataString && parsed.summary) {
          globalSummaryCache[memoryCacheKey] = parsed.summary;
          setSummary(prev => prev !== parsed.summary ? parsed.summary : prev);
          return;
        }
      }
    } catch (e) {
      console.warn("Local storage cache read failed", e);
    }

    if (!isLoading) {
      setIsLoading(true);
      getProgressSummary(sanitizedData, period, lang, vibe)
        .then(text => {
          globalSummaryCache[memoryCacheKey] = text;
          setSummary(prev => prev !== text ? text : prev);
          try {
             localStorage.setItem(storageKey, JSON.stringify({ dataString, summary: text }));
          } catch (e) {
             // Ignore quota errors
          }
          setIsLoading(false);
        })
        .catch(err => {
          const errSummary = err?.message === "QUOTA_EXCEEDED"
            ? (lang === 'ua' ? "Перевищено ліміт запитів до API." : "API quota exceeded.")
            : err?.message === "SERVER_OVERLOADED"
            ? (lang === 'ua' ? "Сервер перевантажений. Спробуйте пізніше." : "Server overloaded. Try again later.")
            : (lang === 'ua' ? "Не вдалося отримати підсумок." : "Failed to get summary.");
          setSummary(prev => prev !== errSummary ? errSummary : prev);
          setIsLoading(false);
        });
    }
  }, [chartDataSerialized, period, lang, isInView, vibe, title]);

  const getIlluminationClass = () => {
    if (!illumination) return "";
    switch (type) {
      case 'caffeine':
        return 'illumination-caffeine';
      case 'water':
      case 'blue':
        return 'illumination-water-blue';
      case 'golden':
      case 'achievement':
        return 'illumination-achievement-golden';
      case 'orange':
      case 'streak':
        return 'illumination-streak-orange';
      case 'food':
        return 'illumination-food';
      case 'both':
      default:
        return 'illumination-both';
    }
  };

  const renderedChildren = isInView ? (typeof children === 'function' ? children(representation) : children) : null;

  return (
    <motion.div 
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative w-full transition-all duration-300 ease-in-out perspective-1000", 
        isOpen ? "h-[360px]" : "h-[68px]",
        !isOpen && "overflow-hidden"
      )}
    >
      <motion.div
        className="relative h-full w-full preserve-3d"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      >
        {/* Front */}
        <div className={cn(
          "absolute h-full w-full backface-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 card-shadow flex flex-col transition-all", 
          getIlluminationClass()
        )}>
          <div 
            className="flex items-center justify-between cursor-pointer select-none"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
              triggerHaptic('SUCCESS');
            }}
          >
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ rotate: isOpen ? 0 : -90 }}
                transition={{ duration: 0.2 }}
                className="text-zinc-400 dark:text-zinc-500 hover:text-emerald-500 transition-colors"
              >
                <ChevronDown className="w-4.5 h-4.5" />
              </motion.div>
              <h3 className="font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-widest text-xs">{title}</h3>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {headerActions}
              {isOpen && typeof children === 'function' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const nextStyle = representation === 'bar' ? 'line' : 'bar';
                    setRepresentation(nextStyle);
                    triggerHaptic('SUCCESS');
                  }}
                  className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer text-zinc-400 hover:text-emerald-500 flex items-center justify-center border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700"
                  title={lang === 'ua' ? "Тип Візуалізації" : "Visualization Style"}
                >
                  {representation === 'bar' ? (
                    <LayoutTemplate className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Activity className="w-4 h-4 text-emerald-500" />
                  )}
                </button>
              )}
              {icon}
            </div>
          </div>

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col min-h-0 overflow-hidden mt-4"
              >
                <div className="flex-1 w-full mb-4 overflow-hidden">
                  <motion.div
                    key={`${period}-${title}-${representation}`}
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
                    className="w-full h-full"
                  >
                    {renderedChildren}
                  </motion.div>
                </div>
                <button 
                  className="bg-emerald-500 text-white px-6 py-2 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 transition-colors self-center shadow-lg shadow-emerald-500/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFlipped(true);
                    triggerHaptic('SUCCESS');
                  }}
                >
                  {lang === 'ua' ? 'Ну-ка' : "Let's dig"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Back */}
        <div className={cn(
          "absolute h-full w-full backface-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 card-shadow flex flex-col rotate-y-180 transition-all overflow-hidden", 
          getIlluminationClass()
        )}>
          <h3 className="font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-widest text-xs mb-2">Let's take a look 🔎</h3>
          <div className="flex-1 flex flex-col items-center justify-between gap-2 min-h-0 overflow-hidden">
            {isLoading && !summary ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900 dark:border-zinc-100"></div>
            ) : (
              <>
                <div className="flex-1 w-full px-2 overflow-hidden flex items-center justify-center">
                  <motion.p 
                    initial="hidden"
                    animate={isFlipped ? "visible" : "hidden"}
                    variants={{
                      visible: {
                        transition: {
                          staggerChildren: 0.015,
                        },
                      },
                    }}
                    className="text-xs font-medium leading-loose text-left line-clamp-[8] text-zinc-800 dark:text-zinc-200"
                  >
                    {summary.split('').map((char, i) => (
                      <motion.span
                        key={i}
                        variants={{
                          hidden: { opacity: 0 },
                          visible: { opacity: 1 },
                        }}
                      >
                        {char}
                      </motion.span>
                    ))}
                  </motion.p>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isFlipped ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 400, damping: 17 }}
                  className="bg-emerald-500 text-white px-8 py-2 rounded-full text-xs font-bold shadow-lg shadow-emerald-500/20 self-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFlipped(false);
                    triggerHaptic('SUCCESS');
                  }}
                >
                  Got it
                </motion.button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
